import type { Geometry } from "geojson";
import { z } from "zod";
import type { StateCreator } from "zustand/vanilla";

import type {
  ItinerarySegment,
  ItineraryWaypoint,
  ItineraryWithDetails,
  WaypointIcon,
} from "@mountayaapp/types/api/api";

/**
 * itinerarySelectionSchema is the focused itinerary entity: a segment (a path)
 * or a waypoint (a point marker), each tagged by `kind` and carrying the
 * entity's id. Zod-first as a discriminated union because the value is shared
 * with the select tools and validated when an agent supplies a selection; a null
 * selection (no entity focused) lives outside the schema as the absence of a
 * value.
 */
export const itinerarySelectionSchema = z.discriminatedUnion("kind", [
  z.object({ id: z.string(), kind: z.literal("segment") }),
  z.object({ id: z.string(), kind: z.literal("waypoint") }),
]);

/**
 * ItinerarySelection is the focused itinerary entity (a segment or a waypoint),
 * derived from itinerarySelectionSchema. The selection slot holds this or null
 * (nothing focused — the whole itinerary shows).
 */
export type ItinerarySelection = z.infer<typeof itinerarySelectionSchema>;

/**
 * Identified is the minimal shape every itinerary list entity shares: a stable
 * id. The segment transforms key off the id alone, never off an index: a segment
 * carries its own zero-based `position`, and the SERVER owns it — re-shaping the
 * chain (inserting, splitting, merging, removing) renumbers positions chain-wide, so
 * the client reads the chain back in the order the server returns and treats
 * `position` as the authority rather than trusting array order to stay put.
 */
interface Identified {
  id: string;
}

/**
 * Positioned is a waypoint's shape for the reorder helpers: a stable id and a
 * zero-based position within the itinerary. A waypoint is standalone rather than
 * chained, so the CLIENT renumbers its position on a reorder and writes the result
 * back — the mirror of a segment, whose position only ever moves server-side.
 */
interface Positioned extends Identified {
  position: number;
}

/**
 * mapSegments rebuilds the itinerary with transform applied to its segment list.
 * Returns null unchanged.
 */
export const mapSegments = (
  itinerary: ItineraryWithDetails | null,
  transform: (segments: ItinerarySegment[]) => ItinerarySegment[],
): ItineraryWithDetails | null => {
  if (!itinerary) {
    return null;
  }

  return { ...itinerary, segments: transform(itinerary.segments) };
};

/**
 * mapWaypoints rebuilds the itinerary with transform applied to its waypoint
 * list. Returns null unchanged.
 */
export const mapWaypoints = (
  itinerary: ItineraryWithDetails | null,
  transform: (waypoints: ItineraryWaypoint[]) => ItineraryWaypoint[],
): ItineraryWithDetails | null => {
  if (!itinerary) {
    return null;
  }

  return { ...itinerary, waypoints: transform(itinerary.waypoints) };
};

/** renumber assigns a zero-based position matching array order. */
export const renumber = <T extends Positioned>(items: T[]): T[] =>
  items.map((item, index) => ({ ...item, position: index }));

/**
 * ItinerarySliceState is the active itinerary, the focused entity within it, and
 * the entity the pointer is currently over on the map.
 */
export interface ItinerarySliceState {
  activeItinerary: ItineraryWithDetails | null;
  /**
   * hoveredEntity is the segment or waypoint the pointer is over ON THE MAP, so
   * the editor list can light the matching row and bring it into view. It is a
   * channel of its own rather than a second writer of the map emphasis: the
   * sidenav owns emphasis (a row hover dims every other line), and a map-side
   * write would clobber that dim on every mouseleave. Here the ownership is the
   * mirror image — the map writes, the list reads — so the two hover directions
   * never contend. Null when the pointer is over no itinerary feature.
   */
  hoveredEntity: ItinerarySelection | null;
  selection: ItinerarySelection | null;
}

/**
 * ItinerarySliceActions are the active-itinerary and selection setters plus the
 * optimistic in-place patches (rename/recolor/geometry) the tool handlers run,
 * reached only through the tool bus.
 */
export interface ItinerarySliceActions {
  moveWaypoint: (waypointId: string, geometry: Geometry) => void;
  recolorSegment: (segmentId: string, color: string) => void;
  /**
   * recolorWaypoint patches the waypoint color in the active itinerary; null
   * clears the explicit color so the marker falls back to the activity default.
   * The optimistic patch runs in the tool handler; the store action keeps the
   * write atomic and renders instantly. A no-op when the waypoint is not in
   * the active itinerary.
   */
  recolorWaypoint: (waypointId: string, color: string | null) => void;
  renameActiveItinerary: (name: string) => void;
  renameSegment: (segmentId: string, name: string) => void;
  /**
   * renameWaypoint patches a waypoint's display name in the active itinerary;
   * null clears the explicit name so the list row falls back to the waypoint
   * id placeholder. A no-op when the waypoint is not in the active itinerary.
   */
  renameWaypoint: (waypointId: string, name: string | null) => void;
  select: (selection: ItinerarySelection | null) => void;
  setActiveItinerary: (itinerary: ItineraryWithDetails | null) => void;
  /**
   * setHoveredEntity records which itinerary entity the pointer is over on the
   * map, or null when it is over none. Purely presentational: it focuses nothing
   * and opens no editor, so it never guards against an edit in progress the way
   * {@link ItinerarySliceActions.select} does.
   */
  setHoveredEntity: (hovered: ItinerarySelection | null) => void;
  setSegmentGeometry: (segmentId: string, geometry: Geometry) => void;
  /**
   * setSegmentSpeed patches a segment's travel speed AND its derived duration in
   * the active itinerary together (the duration follows from the speed for a fixed
   * geometry), so the elevation profile and duration stat update live before the
   * write-through refetch reconciles them with the server truth. The tool handler
   * computes the new duration; the store action keeps the two-field write atomic.
   * A null speed follows the activity default; a null duration means it could not
   * be derived (the catalog has not resolved). A no-op when the segment is not in
   * the active itinerary.
   */
  setSegmentSpeed: (segmentId: string, speed: number | null, duration: number | null) => void;
  /**
   * setWaypointIcon patches the waypoint icon in the active itinerary; null
   * clears the explicit icon so the marker falls back to the activity default
   * glyph. The icon vocabulary is enforced at the bus boundary (a Zod-typed
   * tool input rejects an out-of-set value before it lands here). A no-op when
   * the waypoint is not in the active itinerary.
   */
  setWaypointIcon: (waypointId: string, icon: WaypointIcon | null) => void;
}

/**
 * itineraryInitialState seeds no active itinerary, no selection, and no hover.
 */
export const itineraryInitialState: ItinerarySliceState = {
  activeItinerary: null,
  hoveredEntity: null,
  selection: null,
};

/**
 * createItinerarySlice builds the active-itinerary and selection setters plus
 * the optimistic in-place patches. Every patch no-ops when the entity is not in
 * the active itinerary.
 */
export const createItinerarySlice: StateCreator<
  ItinerarySliceState & ItinerarySliceActions,
  [],
  [],
  ItinerarySliceActions
> = (set) => ({
  moveWaypoint: (waypointId, geometry) =>
    set((state) => ({
      activeItinerary: mapWaypoints(state.activeItinerary, (waypoints) =>
        waypoints.map((waypoint) =>
          waypoint.id === waypointId ? { ...waypoint, geojson: geometry } : waypoint,
        ),
      ),
    })),

  recolorSegment: (segmentId, color) =>
    set((state) => ({
      activeItinerary: mapSegments(state.activeItinerary, (segments) =>
        segments.map((segment) => (segment.id === segmentId ? { ...segment, color } : segment)),
      ),
    })),

  recolorWaypoint: (waypointId, color) =>
    set((state) => ({
      activeItinerary: mapWaypoints(state.activeItinerary, (waypoints) =>
        waypoints.map((waypoint) =>
          waypoint.id === waypointId ? { ...waypoint, color } : waypoint,
        ),
      ),
    })),

  renameActiveItinerary: (name) =>
    set((state) =>
      state.activeItinerary
        ? { activeItinerary: { ...state.activeItinerary, name } }
        : { activeItinerary: state.activeItinerary },
    ),

  renameSegment: (segmentId, name) =>
    set((state) => ({
      activeItinerary: mapSegments(state.activeItinerary, (segments) =>
        segments.map((segment) => (segment.id === segmentId ? { ...segment, name } : segment)),
      ),
    })),

  renameWaypoint: (waypointId, name) =>
    set((state) => ({
      activeItinerary: mapWaypoints(state.activeItinerary, (waypoints) =>
        waypoints.map((waypoint) =>
          waypoint.id === waypointId ? { ...waypoint, name } : waypoint,
        ),
      ),
    })),

  select: (selection) => set({ selection }),
  setActiveItinerary: (activeItinerary) => set({ activeItinerary }),

  setHoveredEntity: (hoveredEntity) =>
    // The pointer sweeping a line fires enter/leave repeatedly over the same
    // feature, so an equal write is dropped rather than reseating the slot and
    // re-rendering every subscribed row.
    set((state) =>
      state.hoveredEntity?.id === hoveredEntity?.id &&
      state.hoveredEntity?.kind === hoveredEntity?.kind
        ? { hoveredEntity: state.hoveredEntity }
        : { hoveredEntity },
    ),

  setSegmentGeometry: (segmentId, geometry) =>
    set((state) => ({
      activeItinerary: mapSegments(state.activeItinerary, (segments) =>
        segments.map((segment) =>
          segment.id === segmentId ? { ...segment, geojson: geometry } : segment,
        ),
      ),
    })),

  setSegmentSpeed: (segmentId, speed, duration) =>
    set((state) => ({
      activeItinerary: mapSegments(state.activeItinerary, (segments) =>
        segments.map((segment) =>
          segment.id === segmentId ? { ...segment, duration, speed } : segment,
        ),
      ),
    })),

  setWaypointIcon: (waypointId, icon) =>
    set((state) => ({
      activeItinerary: mapWaypoints(state.activeItinerary, (waypoints) =>
        waypoints.map((waypoint) =>
          waypoint.id === waypointId ? { ...waypoint, icon } : waypoint,
        ),
      ),
    })),
});
